/**
 * Hook encapsulating CalendarEventModal state for the chat context.
 * Loads device calendars, family members, and calendar mappings,
 * and exposes handlers for opening/saving/dismissing the event modal.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { deviceCalendarService, type DeviceCalendar } from '@/lib/calendar/DeviceCalendarService';
import { NextAvailableSlotFinder } from '@/lib/calendar/NextAvailableSlotFinder';
import { useFamilyMembersStore } from '@/hooks/use-scoped-managers';
import { useCalendarMemberMapping } from '@/hooks/use-calendar-member-mapping';
import type { FamilyMember } from '@/types/family-member';
import { EventDescriptionParser } from '@/lib/utils/EventDescriptionParser';
import type { CreateCalendarEventInput, UpdateCalendarEventInput } from '@/types/events';
import type { VoiceActionItem } from '@/types/voice-transcription';

export interface ChatEventModalState {
  showEventModal: boolean;
  eventActionItem: VoiceActionItem | null;
  isSubmittingEvent: boolean;
  writableCalendars: DeviceCalendar[];
  allCalendars: DeviceCalendar[];
  familyMembers: FamilyMember[];
  calendarMappings: ReturnType<typeof useCalendarMemberMapping>['mappings'];
  openEventModal: (item: VoiceActionItem) => void;
  handleEventSave: (
    input: (CreateCalendarEventInput | UpdateCalendarEventInput) & { targetCalendarId?: string },
  ) => Promise<void>;
  handleEventDismiss: () => void;
}

export function useChatEventModal(): ChatEventModalState {
  const [showEventModal, setShowEventModal] = useState(false);
  const [eventActionItem, setEventActionItem] = useState<VoiceActionItem | null>(null);
  const [isSubmittingEvent, setIsSubmittingEvent] = useState(false);
  const [writableCalendars, setWritableCalendars] = useState<DeviceCalendar[]>([]);
  const [allCalendars, setAllCalendars] = useState<DeviceCalendar[]>([]);
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const { mappings: calendarMappings } = useCalendarMemberMapping();
  const familyMembersStore = useFamilyMembersStore();

  useEffect(() => {
    const loadCalendarsAndMembers = async () => {
      try {
        const calendars = await deviceCalendarService.getDeviceCalendars();
        setAllCalendars(calendars);
        setWritableCalendars(calendars.filter((cal: DeviceCalendar) => cal.allowsModifications));
      } catch (err) {
        console.error('[useChatEventModal] Failed to load calendars:', err);
      }

      if (familyMembersStore) {
        try {
          const members = await familyMembersStore.getAll();
          setFamilyMembers(members);
        } catch (err) {
          console.error('[useChatEventModal] Failed to load family members:', err);
        }
      }
    };
    loadCalendarsAndMembers();
  }, [familyMembersStore]);

  const allCalendarsRef = useRef(allCalendars);
  allCalendarsRef.current = allCalendars;

  const openEventModal = useCallback(async (item: VoiceActionItem) => {
    // Convert subtasks into checkbox description format for event tasks
    let enrichedItem = item;
    if (item.subtasks?.length) {
      const checklistItems = item.subtasks.map((s, i) => ({
        id: `task_${i}`,
        text: s.title,
        completed: false,
        lineIndex: i,
      }));
      const existingLines = item.description ? [item.description] : [];
      const description = EventDescriptionParser.serialize(existingLines, checklistItems);
      enrichedItem = { ...item, description };
    }

    // If no time specified, find next available 30-min slot
    if (!enrichedItem.time) {
      try {
        const calendarIds = allCalendarsRef.current.map(c => c.id);
        const slot = await NextAvailableSlotFinder.findNextSlot(calendarIds);
        enrichedItem = {
          ...enrichedItem,
          date: enrichedItem.date || slot.date,
          time: slot.time,
        };
      } catch {
        // keep enrichedItem as-is
      }
    }

    setEventActionItem(enrichedItem);
    setShowEventModal(true);
  }, []);

  const handleEventSave = useCallback(async (
    input: (CreateCalendarEventInput | UpdateCalendarEventInput) & { targetCalendarId?: string },
  ) => {
    if (!input.targetCalendarId) return;

    setIsSubmittingEvent(true);
    try {
      await deviceCalendarService.createEvent(
        input.targetCalendarId,
        input as CreateCalendarEventInput,
      );
      setShowEventModal(false);
      setEventActionItem(null);
    } catch (err) {
      console.error('[useChatEventModal] Failed to create event:', err);
    } finally {
      setIsSubmittingEvent(false);
    }
  }, []);

  const handleEventDismiss = useCallback(() => {
    setShowEventModal(false);
    setEventActionItem(null);
  }, []);

  return {
    showEventModal,
    eventActionItem,
    isSubmittingEvent,
    writableCalendars,
    allCalendars,
    familyMembers,
    calendarMappings,
    openEventModal,
    handleEventSave,
    handleEventDismiss,
  };
}
